<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card class="rounded-md">
          <v-card-title class="secondary white--text pa-3">
            <v-icon class="mr-3" color="white">mdi-account-hard-hat</v-icon>
            <span class="subtitle-1 white--text font-weight-light">Create New Contractor</span>
            <v-spacer></v-spacer>
            <v-chip class="ma-2" color="white" text-color="secondary">
              Step {{ currentStep }} of 5
            </v-chip>
          </v-card-title>
          
          <v-stepper v-model="currentStep" alt-labels>
            <v-stepper-header>
              <v-stepper-step :complete="currentStep > 1" step="1">
                Contractor Details
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 2" step="2">
                Address
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 3" step="3">
                Bank Details
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step :complete="currentStep > 4" step="4">
                Review
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="5">
                Complete
              </v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content step="1">
                <contractor-details-step
                  v-model="formData.contractorDetails"
                  :loading="loading"
                  @next="nextStep"
                />
              </v-stepper-content>

              <v-stepper-content step="2">
                <contractor-address-step
                  v-model="formData.address"
                  :loading="loading"
                  @next="nextStep"
                  @back="previousStep"
                />
              </v-stepper-content>

              <v-stepper-content step="3">
                <contractor-bank-details-step
                  v-model="formData.bankDetails"
                  :loading="loading"
                  @next="nextStep"
                  @back="previousStep"
                />
              </v-stepper-content>

              <v-stepper-content step="4">
                <contractor-review-step
                  :form-data="formData"
                  :loading="submitting"
                  @submit="submitForm"
                  @back="previousStep"
                />
              </v-stepper-content>

              <v-stepper-content step="5">
                <contractor-complete-step
                  :contractor-id="createdContractorId"
                  @new="resetForm"
                />
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card>
      </v-col>
    </v-row>

    <the-snackbar
      v-model="showError"
      :timeout="5000"
      color="error"
      :text="errorMessage"
    />
  </v-container>
</template>

<script>
import { mapActions } from "vuex";
import ContractorDetailsStep from "@/components/Contractors/Steps/ContractorDetailsStep";
import ContractorAddressStep from "@/components/Contractors/Steps/ContractorAddressStep";
import ContractorBankDetailsStep from "@/components/Contractors/Steps/ContractorBankDetailsStep";
import ContractorReviewStep from "@/components/Contractors/Steps/ContractorReviewStep";
import ContractorCompleteStep from "@/components/Contractors/Steps/ContractorCompleteStep";
import TheSnackbar from "@/components/Snackbars/TheSnackbar";

export default {
  name: "AddContractor",
  components: {
    ContractorDetailsStep,
    ContractorAddressStep,
    ContractorBankDetailsStep,
    ContractorReviewStep,
    ContractorCompleteStep,
    TheSnackbar
  },
  data() {
    return {
      currentStep: 1,
      loading: false,
      showError: false,
      errorMessage: "",
      formData: {
        contractorDetails: {
          title: "",
          forename: "",
          middlename: "",
          surname: "",
          companyName: "",
          email: "",
          mobilePhone: "",
          commissionFee: 0,
          additionalInfo: "",
          contractorTypeId: ""
        },
        address: {
          address1: "",
          address2: "",
          address3: "",
          address4: "",
          postcode: "",
          flatOrRoomNumber: "",
          addressNumber: ""
        },
        bankDetails: {
          accountName: "",
          accountNumber: "",
          sortCode: "",
          legacyBacsReference: ""
        }
      }
    };
  },
  computed: {
    submitting() {
      return this.$store.state.ContractorsManagement.submitting;
    },
    createdContractorId() {
      return this.$store.state.ContractorsManagement.createdContractorId;
    }
  },
  methods: {
    ...mapActions("ContractorsManagement", [
      "createContractor",
      "resetContractorManagement",
      "fetchContractorTypes"
    ]),
    nextStep() {
      this.currentStep++;
    },
    previousStep() {
      this.currentStep--;
    },
    async submitForm() {
      try {
        // Transform to PascalCase as expected by the API
        const payload = {
          ContractorName: {
            Title: this.formData.contractorDetails.title,
            Forename: this.formData.contractorDetails.forename,
            Middlename: this.formData.contractorDetails.middlename,
            Surname: this.formData.contractorDetails.surname
          },
          CompanyName: this.formData.contractorDetails.companyName,
          Address: {
            Address1: this.formData.address.address1,
            Address2: this.formData.address.address2,
            Address3: this.formData.address.address3,
            Address4: this.formData.address.address4,
            Postcode: this.formData.address.postcode,
            FlatOrRoomNumber: this.formData.address.flatOrRoomNumber,
            AddressNumber: this.formData.address.addressNumber
          },
          BankDetails: {
            AccountName: this.formData.bankDetails.accountName,
            AccountNumber: this.formData.bankDetails.accountNumber,
            SortCode: this.formData.bankDetails.sortCode,
            LegacyBacsReference: this.formData.bankDetails.legacyBacsReference
          },
          MobilePhone: this.formData.contractorDetails.mobilePhone,
          Email: this.formData.contractorDetails.email,
          CommissionFee: parseFloat(this.formData.contractorDetails.commissionFee),
          AdditionalInfo: this.formData.contractorDetails.additionalInfo,
          ContractorTypeID: this.formData.contractorDetails.contractorTypeId
        };

        await this.createContractor(payload);
        
        // Wait for the store to update before proceeding
        await this.$nextTick();
        
        // Only move to step 5 if we have a valid contractor ID in the store
        if (this.createdContractorId) {
          this.currentStep = 5;
        } else {
          throw new Error("No contractor ID returned from API");
        }
      } catch (error) {
        this.errorMessage = error.message || "Failed to create contractor";
        this.showError = true;
        // Keep user on step 4 if there's an error
        this.currentStep = 4;
      }
    },
    resetForm() {
      this.currentStep = 1;
      this.formData = {
        contractorDetails: {
          title: "",
          forename: "",
          middlename: "",
          surname: "",
          companyName: "",
          email: "",
          mobilePhone: "",
          commissionFee: 0,
          additionalInfo: "",
          contractorTypeId: ""
        },
        address: {
          address1: "",
          address2: "",
          address3: "",
          address4: "",
          postcode: "",
          flatOrRoomNumber: "",
          addressNumber: ""
        },
        bankDetails: {
          accountName: "",
          accountNumber: "",
          sortCode: "",
          legacyBacsReference: ""
        }
      };
      this.resetContractorManagement();
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.currentStep < 5 && this.currentStep > 1) {
      const answer = window.confirm(
        "Do you really want to leave? You have unsaved changes!"
      );
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  mounted() {
    this.resetContractorManagement();
    // Clear any global errors that might be lingering
    this.$store.commit("CLEAR_ERRORS");
    // Fetch contractor types for the form
    this.fetchContractorTypes();
  }
};
</script>

<style scoped>
</style>